import { ThemeProvider, TooltipProvider } from "@microbeta/ui";
import { QueryClientProvider as TanstackQueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import type { FunctionComponent, PropsWithChildren } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider } from "react-i18next";
import { ErrorBoundary } from "@/shared/components";
import { CookieConsentBanner } from "@/shared/components/CookieConsentBanner";
import { AuthProvider } from "../../features/auth/AuthContext";
import i18n from "../../features/i18n/i18n";
import { PurchasesProvider } from "../../features/purchases";
import { queryClient } from "./react-query";

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24",
  capture_exceptions: true,
  autocapture: false, // Disable autocapture as requested
  capture_pageview: false, // Use custom events for pageviews if needed
  opt_out_capturing_by_default: true, // Respect privacy by default
  sanitize_properties: (properties) => {
    // Mask common PII patterns in properties
    const piiKeys = ["email", "name", "phoneNumber", "address"];
    piiKeys.forEach((key) => {
      if (properties[key]) {
        properties[key] = "[MASKED]";
      }
    });

    // Mask PII in URLs (e.g., email as query param)
    if (properties.$current_url) {
      properties.$current_url = (properties.$current_url as string).replace(
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
        "[MASKED_EMAIL]"
      );
    }
    return properties;
  },
  debug: import.meta.env.MODE === "development" && false,
});

export const ContextProviders: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "test-key";

  return (
    <ErrorBoundary>
      <PostHogProvider client={posthog}>
        <HelmetProvider>
          <I18nextProvider i18n={i18n}>
            <TanstackQueryClientProvider client={queryClient}>
              <ThemeProvider>
                <TooltipProvider>
                  <GoogleReCaptchaProvider
                    reCaptchaKey={recaptchaKey}
                    useEnterprise={true}
                    scriptProps={{ defer: true }}
                  >
                    <AuthProvider>
                      <PurchasesProvider>
                        {children}
                        <CookieConsentBanner />
                      </PurchasesProvider>
                    </AuthProvider>
                  </GoogleReCaptchaProvider>
                </TooltipProvider>
              </ThemeProvider>
            </TanstackQueryClientProvider>
          </I18nextProvider>
        </HelmetProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
};
